import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { IApiResponse } from '@core/interfaces/api-response.interface';
import { EMaterialType } from '@enums/campaign-analytics.enum';
import { ITimeline } from '@interfaces/campaign-analytics-api.interface';
import { ComponentStore } from '@ngrx/component-store';
import { EMPTY, MonoTypeOperatorFunction, Observable, pipe } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { DeliverableAnalyticsContentService } from '../../deliverable-analytics-content.service';

interface IStreamDetailsState {
  timeline: ITimeline[] | null;
  loading: boolean;
  error: string | null;
}

const initialState: IStreamDetailsState = {
  error: null,
  loading: false,
  timeline: null,
};

@Injectable()
export class StreamDetailsStore extends ComponentStore<IStreamDetailsState> {
  public readonly loading$ = this.selectSignal((state: IStreamDetailsState) => state.loading);
  public readonly error$ = this.selectSignal((state: IStreamDetailsState) => state.error);
  public readonly timeline$ = this.selectSignal((state: IStreamDetailsState) => state.timeline);

  private readonly router = inject(Router);

  private readonly handleError = (errorMessage: string): MonoTypeOperatorFunction<unknown> =>
    pipe(
      catchError(() => {
        this.patchState({ error: errorMessage, loading: false, timeline: null });

        return EMPTY;
      }),
    );

  public readonly fetchTimeline = this.effect((trigger$: Observable<{ socialContentId: string }>) => {
    return trigger$.pipe(
      tap(() => this.patchState({ error: null, loading: true, timeline: null })),
      switchMap(({ socialContentId }) => {
        const exportId = this.getExportId();
        const fetch$ = exportId
          ? this.deliverableAnalyticsContentService.getMaterialForExport<ITimeline[]>(
              exportId,
              EMaterialType.timeline,
              socialContentId,
            )
          : this.deliverableAnalyticsContentService.getMaterial<ITimeline[]>(EMaterialType.timeline, socialContentId);

        return fetch$.pipe(
          tap((timeline: IApiResponse<ITimeline[]>) => {
            this.patchState({ loading: false, timeline: timeline.data });
          }),
          this.handleError('Failed to load timeline'),
        );
      }),
    );
  });

  constructor(private readonly deliverableAnalyticsContentService: DeliverableAnalyticsContentService) {
    super(initialState);
  }

  private getExportId(): string | null {
    const { url } = this.router;

    return url.includes('campaigns/export') ? (url.split('/').pop() ?? null) : null;
  }
}
